import React, { useState } from 'react';
import styled from 'styled-components';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import IconWrapper from '../common/IconWrapper';
import { 
  FaTachometerAlt, 
  FaProjectDiagram, 
  FaMoneyBillWave, 
  FaFileInvoiceDollar,
  FaBell, 
  FaUser, 
  FaCog, 
  FaSignOutAlt,
  FaBars,
  FaTimes
} from 'react-icons/fa';

// Styled Components
const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: var(--background-color);
`;

const Sidebar = styled.aside<{ isOpen: boolean }>`
  width: 280px;
  min-width: 280px;
  max-width: 280px;
  background: linear-gradient(135deg, var(--primary-color) 0%, #e07a10 100%);
  color: white;
  transition: all 0.3s ease;
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  z-index: 1000;
  overflow: hidden;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(-100%)'};
    width: 280px;
    min-width: 280px;
    max-width: 280px;
  }
`;

const SidebarHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  .logo-icon {
    font-size: 1.5rem;
  }
  
  .logo-text {
    font-weight: 600;
    font-size: 1.1rem;
  }
  
  .logo-subtitle {
    font-size: 0.8rem;
    opacity: 0.9;
  }
`;

const SidebarContent = styled.div`
  padding: 1rem 0;
  flex: 1;
  overflow-y: auto;
  margin: 0;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 2px;
    
    &:hover {
      background: rgba(255, 255, 255, 0.5);
    }
  }
`;

const MenuItem = styled(Link)<{ $active: boolean }>`
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  color: ${props => props.$active ? 'white' : 'rgba(255, 255, 255, 0.8)'};
  text-decoration: none;
  transition: all 0.3s ease;
  border-left: 3px solid ${props => props.$active ? 'white' : 'transparent'};
  margin: 0.25rem 0;
  
  &:hover {
    color: white;
    background-color: rgba(255, 255, 255, 0.1);
    border-left-color: rgba(255, 255, 255, 0.5);
  }
  
  .menu-icon {
    margin-right: 0.75rem;
    font-size: 1.1rem;
    min-width: 20px;
  }
  
  .menu-text {
    font-weight: 500;
    font-size: 0.95rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 180px;
  }
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: 280px;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const Header = styled.header`
  background: white;
  border-bottom: 1px solid #e9ecef;
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: var(--secondary-color);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: var(--border-radius);
  transition: all 0.3s ease;
  
  &:hover {
    background-color: var(--light-gray);
  }
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const UserName = styled.div`
  font-weight: 600;
  color: var(--secondary-color);
  font-size: 1rem;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const UserRole = styled.div`
  font-size: 0.9rem;
  color: var(--dark-gray);
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const LogoutButton = styled.button`
  background: none;
  border: none;
  color: var(--danger-color);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius);
  transition: all 0.3s ease;
  font-size: 0.9rem;
  
  &:hover {
    background-color: var(--danger-color);
    color: white;
  }
  
  @media (max-width: 768px) {
    padding: 0.4rem 0.8rem;
    font-size: 0.8rem;
  }
`;

const MobileOverlay = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'block' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  
  @media (max-width: 768px) {
    display: ${props => props.isOpen ? 'block' : 'none'};
  }
`;

const ContentArea = styled.div`
  flex: 1;
  padding: 1.5rem;
  background-color: var(--background-color);
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`;

const Footer = styled.div`
  padding: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  text-align: center;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.75rem;
  background: rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
  position: relative;
  z-index: 2;
  
  .developer-info {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
  }
  
  .developer-name {
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.8rem;
  }
  
  .developer-tagline {
    font-size: 0.65rem;
    color: rgba(255, 255, 255, 0.6);
    font-style: italic;
  }
  
  .copyright {
    font-size: 0.6rem;
    color: rgba(255, 255, 255, 0.5);
    margin-top: 0.25rem;
  }
`;

const SupervisorLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const menuItems = [
    { path: '/supervisor', icon: <IconWrapper icon={FaTachometerAlt} className="menu-icon" />, text: 'Dashboard' },
    { path: '/supervisor/project', icon: <IconWrapper icon={FaProjectDiagram} className="menu-icon" />, text: 'My Project' },
    { path: '/supervisor/funds', icon: <IconWrapper icon={FaMoneyBillWave} className="menu-icon" />, text: 'Fund Requests' },
    { path: '/supervisor/accounting', icon: <IconWrapper icon={FaFileInvoiceDollar} className="menu-icon" />, text: 'Accounting' },
    { path: '/supervisor/notifications', icon: <IconWrapper icon={FaBell} className="menu-icon" />, text: 'Notifications' },
    { path: '/supervisor/profile', icon: <IconWrapper icon={FaUser} className="menu-icon" />, text: 'Profile' },
    { path: '/supervisor/settings', icon: <IconWrapper icon={FaCog} className="menu-icon" />, text: 'Settings' },
  ];

  return (
    <LayoutContainer>
      {/* Mobile Overlay */}
      <MobileOverlay isOpen={isMobileMenuOpen} onClick={closeMobileMenu} />

      {/* Sidebar */}
      <Sidebar isOpen={isMobileMenuOpen}>
        <SidebarHeader>
          <div className="logo-icon">
            <img src="/pfrs.jpeg" alt="PFRS Logo" style={{ width: '40px', height: '40px', borderRadius: '8px' }} />
          </div>
          <div>
            <div className="logo-text">PFRS</div>
            <div className="logo-subtitle">Supervisor Portal</div>
          </div>
        </SidebarHeader>

        <SidebarContent>
          {menuItems.map((item) => (
            <MenuItem 
              key={item.path} 
              to={item.path} 
              $active={location.pathname === item.path}
              onClick={closeMobileMenu}
            >
              {item.icon}
              <span className="menu-text">{item.text}</span>
            </MenuItem>
          ))}
        </SidebarContent>

        <Footer>
          <div className="developer-info">
            <div className="developer-name">Developed by Sandfish</div>
            <div className="developer-tagline">Building Digital Solutions</div>
            <div className="copyright">
              &copy; {new Date().getFullYear()} PFRS
            </div>
          </div>
        </Footer>
      </Sidebar>

      {/* Main Content */}
      <MainContent>
        <Header>
          <HeaderLeft>
            <MobileMenuButton onClick={toggleMobileMenu}>
              <IconWrapper icon={isMobileMenuOpen ? FaTimes : FaBars} />
            </MobileMenuButton>
            <UserInfo>
              <div>
                <UserName>{user?.name}</UserName>
                <UserRole>Supervisor</UserRole>
              </div>
            </UserInfo>
          </HeaderLeft>
          <LogoutButton onClick={handleLogout}>
            <IconWrapper icon={FaSignOutAlt} />
            Logout
          </LogoutButton>
        </Header>

        <ContentArea>
          <Outlet />
        </ContentArea>
      </MainContent>
    </LayoutContainer>
  );
};

export default SupervisorLayout; 